import React, { useEffect, useState } from "react";
import { printingApi } from "../../lib/printing/printingApi";
import {
  DOC_TYPES, DOC_TYPE_LABELS, sectionLabel, layoutPayload, validateLayoutForm, reprintLabelFor, operatorMessage, formatWhen, LAYOUT_TEXT_LIMITS,
} from "../../lib/printing/printingModel";
import { SectionCard, Field, CheckField, inputCls, btnPrimary, btnSecondary, Pill, Notice, EmptyState, Loading } from "./PrintingUi";

const PRIVACY_NOTE = {
  customer_receipt: "Customer phone and email print masked, if enabled. Card numbers and tokens can never be printed.",
  kitchen_ticket: "Customer name and phone are off by default on kitchen tickets. Prices are off by default.",
  packing_slip: "Customer phone is masked if enabled. Prices are off by default on packing slips.",
};

function formFromLayout(layout, allowed) {
  const sections = {};
  Object.keys(allowed || {}).forEach((k) => { sections[k] = layout?.sections?.[k] ?? allowed[k]; });
  return {
    name: layout?.name && layout.source !== "system" ? layout.name : "",
    sections,
    header_text: (layout?.header_lines || []).join("\n"),
    footer_text: (layout?.footer_lines || []).join("\n"),
    policy_text: layout?.policy_text || "",
    promo_footer: layout?.promo_footer || "",
    thank_you_text: layout?.thank_you_text || "",
  };
}

export default function LayoutsPanel({ layouts, sections, isAdmin, onChanged, notify }) {
  const [docType, setDocType] = useState("customer_receipt");
  const allowed = sections?.sections?.[docType] || {};
  const current = layouts?.current?.[docType] || null;
  const history = (layouts?.layouts || []).filter((l) => l.doc_type === docType).sort((a, b) => (b.version || 0) - (a.version || 0));
  const [form, setForm] = useState(() => formFromLayout(current, allowed));
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const dirty = JSON.stringify(form) !== JSON.stringify(formFromLayout(current, allowed));

  useEffect(() => {
    setForm(formFromLayout(current, allowed)); setErrors({}); setApiError(null); setPreview(null); setViewing(null);
    // `current` and `allowed` are derived from layouts/sections/docType, which are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, layouts, sections]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleSection = (k, v) => setForm((f) => ({ ...f, sections: { ...f.sections, [k]: v } }));

  const save = async () => {
    const v = validateLayoutForm(form);
    setErrors(v.errors);
    if (!v.valid) return;
    setBusy(true); setApiError(null);
    try {
      const data = await printingApi.createLayoutVersion(docType, layoutPayload(docType, allowed, form));
      notify("success", `${DOC_TYPE_LABELS[docType]} layout saved as version ${data.layout?.version}. Earlier versions are kept; queued jobs keep theirs.`);
      onChanged();
    } catch (err) { setApiError(operatorMessage(err)); }
    finally { setBusy(false); }
  };

  const loadPreview = async (layout) => {
    setPreviewBusy(true); setApiError(null);
    try {
      const body = { doc_type: docType };
      if (layout && layout.source !== "system") { body.layout_id = layout.id; body.layout_version = layout.version; }
      setPreview({ layout, ...(await printingApi.preview(body)) });
    } catch (err) { setApiError(operatorMessage(err)); }
    finally { setPreviewBusy(false); }
  };

  const draftLines = () => {
    const on = Object.entries(form.sections).filter(([, v]) => v).map(([k]) => sectionLabel(k));
    return [
      ...(form.header_text ? form.header_text.split("\n") : []),
      `[${DOC_TYPE_LABELS[docType]} — sections: ${on.join(", ") || "none"}]`,
      form.policy_text, form.promo_footer, form.thank_you_text,
      ...(form.footer_text ? form.footer_text.split("\n") : []),
      `Reprints are labelled ${reprintLabelFor(docType)}.`,
    ].filter(Boolean);
  };

  return (
    <div className="space-y-4" data-testid="layouts-panel">
      <SectionCard title="Document layouts" description="Controlled, versioned layouts. Saving always creates a new version; older versions stay viewable and queued jobs keep the version they were created with.">
        <Field label="Document type">
          <select className={inputCls} value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="layouts-doctype">{DOC_TYPES.map((d) => <option key={d} value={d}>{DOC_TYPE_LABELS[d]}</option>)}</select>
        </Field>
        {!sections ? <Loading label="Loading layout options…" /> : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold">Active version:</span>
                <Pill tone="success" testId="layout-active-version">{current ? `v${current.version} · ${current.name}` : "System default (v0)"}</Pill>
                {current?.created_at && <span className="text-xs text-stone-600">saved {formatWhen(current.created_at)}</span>}
              </div>
              <Notice tone="info" testId={`privacy-note-${docType}`}>{PRIVACY_NOTE[docType]} Reprints of this document are labelled <strong>{reprintLabelFor(docType)}</strong>.</Notice>
              {apiError && <Notice tone="danger" testId="layout-error">{apiError}</Notice>}
              <Field label="Layout name" error={errors.name}><input className={inputCls} value={form.name} onChange={set("name")} disabled={!isAdmin} maxLength={LAYOUT_TEXT_LIMITS.maxNameChars} data-testid="layout-name" placeholder="e.g. Standard receipt" /></Field>
              <fieldset className="rounded-md border border-stone-200 p-3">
                <legend className="px-1 text-sm font-semibold">Sections to print</legend>
                <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                  {Object.keys(allowed).map((k) => (
                    <CheckField key={k} label={sectionLabel(k)} checked={Boolean(form.sections[k])} onChange={(v) => toggleSection(k, v)} disabled={!isAdmin} testId={`section-${docType}-${k}`} />
                  ))}
                </div>
                <p className="text-xs text-stone-600">Only the sections listed here exist for this document. Card numbers, tokens and unmasked phone numbers are never available.</p>
              </fieldset>
              <Field label="Header lines" hint={`Up to ${LAYOUT_TEXT_LIMITS.maxLines} lines, ${LAYOUT_TEXT_LIMITS.maxLineChars} characters each. Plain text only.`} error={errors.header_text}>
                <textarea className={`${inputCls} h-24 py-2`} value={form.header_text} onChange={set("header_text")} disabled={!isAdmin} data-testid="layout-header" />
              </Field>
              <Field label="Thank-you text" error={errors.thank_you_text}><input className={inputCls} value={form.thank_you_text} onChange={set("thank_you_text")} disabled={!isAdmin} data-testid="layout-thankyou" maxLength={LAYOUT_TEXT_LIMITS.maxLineChars} /></Field>
              <Field label="Policy text" hint="Returns, allergens, or terms." error={errors.policy_text}><input className={inputCls} value={form.policy_text} onChange={set("policy_text")} disabled={!isAdmin} data-testid="layout-policy" maxLength={LAYOUT_TEXT_LIMITS.maxLineChars} /></Field>
              <Field label="Promotional / coupon footer" error={errors.promo_footer}><input className={inputCls} value={form.promo_footer} onChange={set("promo_footer")} disabled={!isAdmin} data-testid="layout-promo" maxLength={LAYOUT_TEXT_LIMITS.maxLineChars} /></Field>
              <Field label="Footer lines" error={errors.footer_text}><textarea className={`${inputCls} h-20 py-2`} value={form.footer_text} onChange={set("footer_text")} disabled={!isAdmin} data-testid="layout-footer" /></Field>
              <div className="flex flex-wrap gap-2">
                <button type="button" className={btnSecondary} onClick={() => loadPreview(current)} disabled={previewBusy} data-testid="layout-preview-current">{previewBusy ? "Loading…" : "Preview active version (printed text)"}</button>
                {isAdmin && <button type="button" className={btnPrimary} onClick={save} disabled={busy || !dirty} data-testid="layout-save">{busy ? "Saving…" : "Save as new version"}</button>}
              </div>
            </div>
            <div className="space-y-3">
              <div className="rounded-md border border-stone-200 bg-stone-50 p-3" data-testid="layout-draft-summary">
                <div className="text-sm font-semibold">Draft summary (unsaved)</div>
                <p className="text-xs text-stone-600">A quick check of what you are about to save. The authoritative printed text comes from the server preview once a version is saved.</p>
                <pre tabIndex={0} aria-label="Draft summary" className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded border border-stone-300 bg-white p-2 font-mono text-xs">{draftLines().join("\n")}</pre>
              </div>
              {preview && (
                <div className="rounded-md border border-stone-200 bg-stone-50 p-3" data-testid="layout-preview">
                  <div className="text-sm font-semibold">Printed text — {preview.layout ? `v${preview.layout.version} ${preview.layout.name}` : "system default"}</div>
                  <pre tabIndex={0} aria-label="Printed text preview" className="mt-2 max-h-72 overflow-auto rounded border border-stone-300 bg-white p-2 font-mono text-xs">{preview.text_preview}</pre>
                </div>
              )}
              <div>
                <div className="text-sm font-semibold">Version history</div>
                {history.length === 0 ? <EmptyState testId="layout-history-empty">No saved versions yet — the system default is in use.</EmptyState> : (
                  <ul className="mt-1 divide-y divide-stone-200 rounded-md border border-stone-200" data-testid="layout-history">
                    {history.map((l) => (
                      <li key={`${l.id}-${l.version}`} className="flex flex-wrap items-center justify-between gap-2 p-2 text-sm" data-testid={`layout-version-${l.version}`}>
                        <span>v{l.version} · {l.name} {l.is_current && <Pill tone="success">Active</Pill>}<span className="block text-xs text-stone-600">{formatWhen(l.created_at)}</span></span>
                        <span className="flex gap-2">
                          <button type="button" className={btnSecondary} onClick={() => setViewing(viewing?.id === l.id && viewing?.version === l.version ? null : l)} data-testid={`layout-view-${l.version}`}>{viewing?.id === l.id && viewing?.version === l.version ? "Hide" : "View"}</button>
                          <button type="button" className={btnSecondary} onClick={() => loadPreview(l)} data-testid={`layout-preview-${l.version}`}>Preview</button>
                        </span>
                        {viewing?.id === l.id && viewing?.version === l.version && (
                          <dl className="w-full grid grid-cols-1 gap-1 text-xs sm:grid-cols-2" data-testid={`layout-detail-${l.version}`}>
                            <div><dt className="inline text-stone-500">Sections on: </dt><dd className="inline">{Object.entries(l.sections || {}).filter(([, v]) => v).map(([k]) => sectionLabel(k)).join(", ")}</dd></div>
                            <div><dt className="inline text-stone-500">Thank-you: </dt><dd className="inline">{l.thank_you_text || "—"}</dd></div>
                            <div><dt className="inline text-stone-500">Policy: </dt><dd className="inline">{l.policy_text || "—"}</dd></div>
                            <div><dt className="inline text-stone-500">Promo: </dt><dd className="inline">{l.promo_footer || "—"}</dd></div>
                          </dl>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
